// Reusable network builders for Domain 01. Each returns a fresh network object
// plus diagram layout. Labs clone and then modify these (faults, missing config).

import { pc, server, sw, router, link, network, setIf } from '../../engine/net/model.js';

/** Internet edge: ISP router + public DNS + public web server. */
export function internet() {
  const ISP = router('ISP', { seq: 9, managed: false });
  setIf(ISP, 'GigabitEthernet0/0', { shutdown: false, ip: { ip: '203.0.113.1', mask: '255.255.255.252' }, description: 'CUST-BRANCH01' });
  setIf(ISP, 'GigabitEthernet0/1', { shutdown: false, ip: { ip: '8.8.8.1', mask: '255.255.255.0' } });
  setIf(ISP, 'GigabitEthernet0/2', { shutdown: false, ip: { ip: '93.184.216.1', mask: '255.255.255.0' } });
  // ISP knows nothing about customer private space (RFC 1918) — NAT is mandatory.
  const DNS1 = server('DNS1', {
    mac: '00d0ba111101', ttl: 64, ip: '8.8.8.8', mask: '255.255.255.0', gw: '8.8.8.1', dns: ['8.8.8.8'],
    services: [{ proto: 'udp', port: 53, name: 'dns' }, { proto: 'tcp', port: 53, name: 'dns' }],
    dnsRecords: { 'www.example.com': '93.184.216.34', 'example.com': '93.184.216.34', 'dns.google': '8.8.8.8', 'intranet.branch.local': '10.10.20.10', 'files.branch.local': '10.10.20.10' },
  });
  DNS1.dnsName = 'dns.google';
  const WEB1 = server('WEB1', {
    mac: '00d0ba222201', ttl: 64, ip: '93.184.216.34', mask: '255.255.255.0', gw: '93.184.216.1',
    services: [{ proto: 'tcp', port: 80, name: 'http' }, { proto: 'tcp', port: 443, name: 'https' }],
    http: { title: 'Example Domain', body: 'This domain is for use in illustrative examples.', server: 'ECS (sec/96EE)' },
  });
  return {
    devices: [ISP, DNS1, WEB1],
    links: [
      link('ISP', 'GigabitEthernet0/1', 'DNS1', 'FastEthernet0', { type: 'Fiber', connector: 'LC' }),
      link('ISP', 'GigabitEthernet0/2', 'WEB1', 'FastEthernet0', { type: 'Fiber', connector: 'LC' }),
    ],
  };
}

/**
 * Branch office: PC1, PC2 → SW1 → R1 → ISP → Internet.
 * opts.vlan: put user ports + router port in this VLAN (default 1)
 * opts.routerConfigured: R1 fully configured (IP, NAT, default route)
 */
export function branch(opts = {}) {
  const vlan = opts.vlan || 1;
  const PC1 = pc('PC1', { mac: '005079666800', ip: '192.168.10.10', mask: '255.255.255.0', gw: '192.168.10.1', dns: ['8.8.8.8'] });
  const PC2 = pc('PC2', { mac: '005079666801', ip: '192.168.10.11', mask: '255.255.255.0', gw: '192.168.10.1', dns: ['8.8.8.8'] });
  const SW1 = sw('SW1', { seq: 1, vlans: vlan !== 1 ? { [vlan]: { name: opts.vlanName || 'USERS' }, 20: { name: 'PRINTERS' } } : {} });
  setIf(SW1, 'FastEthernet0/1', { description: 'PC1-DESK-14', accessVlan: vlan, explicitAccess: vlan !== 1 });
  setIf(SW1, 'FastEthernet0/2', { description: 'PC2-DESK-15', accessVlan: vlan, explicitAccess: vlan !== 1 });
  setIf(SW1, 'FastEthernet0/24', { description: 'UPLINK-R1-G0/0', accessVlan: vlan, explicitAccess: vlan !== 1 });
  const R1 = router('R1', { seq: 2 });
  if (opts.routerConfigured !== false) {
    setIf(R1, 'GigabitEthernet0/0', { shutdown: false, description: 'LAN-SW1-Fa0/24', ip: { ip: '192.168.10.1', mask: '255.255.255.0' }, nat: 'inside' });
    setIf(R1, 'GigabitEthernet0/1', { shutdown: false, description: 'WAN-ISP-CIRCUIT-4471', ip: { ip: '203.0.113.2', mask: '255.255.255.252' }, nat: 'outside' });
    R1.routes.push({ net: '0.0.0.0', mask: '0.0.0.0', nh: '203.0.113.1' });
    R1.acls['1'] = [{ action: 'permit', base: '192.168.10.0', wild: '0.0.0.255', text: '192.168.10.0 0.0.0.255' }];
    R1.nat.rules.push({ acl: '1', ifc: 'GigabitEthernet0/1', overload: true });
  }
  const inet = internet();
  return network([PC1, PC2, SW1, R1, ...inet.devices], [
    link('PC1', 'FastEthernet0', 'SW1', 'FastEthernet0/1'),
    link('PC2', 'FastEthernet0', 'SW1', 'FastEthernet0/2'),
    link('SW1', 'FastEthernet0/24', 'R1', 'GigabitEthernet0/0'),
    link('R1', 'GigabitEthernet0/1', 'ISP', 'GigabitEthernet0/0', { type: 'Cat6 UTP (ISP handoff)', length: 5 }),
    ...inet.links,
  ]);
}

/** Diagram layout for the branch topology (coordinates in a 900×360 viewBox). */
export const branchLayout = {
  width: 980, height: 380,
  nodes: {
    PC1: { x: 70, y: 110, type: 'pc', sub: '192.168.10.10/24' },
    PC2: { x: 70, y: 270, type: 'pc', sub: '192.168.10.11/24' },
    SW1: { x: 290, y: 190, type: 'switch', sub: 'Catalyst 2960' },
    R1: { x: 500, y: 190, type: 'router', sub: 'Branch edge · ISR 2911' },
    ISP: { x: 700, y: 190, type: 'router', sub: 'Provider edge (no access)', dim: true },
    DNS1: { x: 900, y: 100, type: 'server', sub: '8.8.8.8 · DNS' },
    WEB1: { x: 900, y: 285, type: 'server', sub: '93.184.216.34 · web' },
  },
  zones: [
    { x: 20, y: 30, w: 560, h: 330, label: 'BRANCH-01 LAN · 192.168.10.0/24' },
    { x: 610, y: 30, w: 360, h: 330, label: 'INTERNET', cloud: true },
  ],
  linkLabels: {
    'R1:GigabitEthernet0/1': '203.0.113.2/30', 'ISP:GigabitEthernet0/0': '.1',
    'R1:GigabitEthernet0/0': '192.168.10.1',
  },
};

/** Access-layer star: PCs on one switch (no router unless `withRouter`). */
export function star({ pcs = ['PC1', 'PC2', 'PC3'], ports = [1, 2, 3], withRouter = false, routerUp = true } = {}) {
  const devs = [], links = [];
  const SW1 = sw('SW1', { seq: 1 });
  pcs.forEach((id, i) => {
    const n = i + 10;
    devs.push(pc(id, { mac: `00507966${(0x68 + i).toString(16)}0${i}`, ip: `192.168.10.${n}`, mask: '255.255.255.0', gw: '192.168.10.1', dns: ['8.8.8.8'] }));
    setIf(SW1, `FastEthernet0/${ports[i]}`, { description: `${id}-DESK` });
    links.push(link(id, 'FastEthernet0', 'SW1', `FastEthernet0/${ports[i]}`));
  });
  devs.push(SW1);
  if (withRouter) {
    const R1 = router('R1', { seq: 2 });
    if (routerUp) setIf(R1, 'GigabitEthernet0/0', { shutdown: false, description: 'LAN-SW1-Fa0/24', ip: { ip: '192.168.10.1', mask: '255.255.255.0' } });
    setIf(SW1, 'FastEthernet0/24', { description: 'UPLINK-R1-G0/0' });
    devs.push(R1);
    links.push(link('SW1', 'FastEthernet0/24', 'R1', 'GigabitEthernet0/0'));
  }
  return network(devs, links);
}

/** Two-LAN campus: PC1 → SW1 → R1 → SW2 → SRV1 (server subnet 10.10.20.0/24). */
export function twoLan(o = {}) {
  const PC1 = pc('PC1', { mac: '005079666800', ip: '192.168.10.10', mask: '255.255.255.0', gw: '192.168.10.1', dns: [o.dns || '10.10.20.10'] });
  const SW1 = sw('SW1', { seq: 1 });
  const SW2 = sw('SW2', { seq: 3 });
  const R1 = router('R1', { seq: 2 });
  setIf(SW1, 'FastEthernet0/1', { description: 'PC1-ACCOUNTING' });
  setIf(SW1, 'FastEthernet0/24', { description: 'UPLINK-R1-G0/0' });
  setIf(SW2, 'FastEthernet0/10', { description: 'SRV1-FILES' });
  setIf(SW2, 'FastEthernet0/24', { description: 'UPLINK-R1-G0/1' });
  setIf(R1, 'GigabitEthernet0/0', { shutdown: false, description: 'USERS-SW1', ip: { ip: '192.168.10.1', mask: '255.255.255.0' } });
  setIf(R1, 'GigabitEthernet0/1', { shutdown: false, description: 'SERVERS-SW2', ip: { ip: '10.10.20.1', mask: '255.255.255.0' } });
  const SRV1 = server('SRV1', {
    mac: '00155d0a1410', ip: '10.10.20.10', mask: '255.255.255.0', gw: o.srvGw || '10.10.20.1', dns: ['10.10.20.10'], user: 'Administrator',
    services: o.services || [
      { proto: 'udp', port: 53, name: 'dns', pid: 2412 }, { proto: 'tcp', port: 53, name: 'dns', pid: 2412 }, { proto: 'tcp', port: 80, name: 'http', pid: 4 },
      { proto: 'tcp', port: 445, name: 'smb', pid: 4 }, { proto: 'tcp', port: 3389, name: 'rdp', pid: 1188 },
    ],
    dnsRecords: { 'intranet.branch.local': '10.10.20.10', 'files.branch.local': '10.10.20.10', 'srv1.branch.local': '10.10.20.10' },
    http: { title: 'BRANCH-01 Intranet', body: 'Welcome to the intranet', server: 'Microsoft-IIS/10.0' },
  });
  SRV1.dnsName = 'srv1.branch.local';
  SRV1.authoritative = true;
  return network([PC1, SW1, R1, SW2, SRV1], [
    link('PC1', 'FastEthernet0', 'SW1', 'FastEthernet0/1'),
    link('SW1', 'FastEthernet0/24', 'R1', 'GigabitEthernet0/0'),
    link('R1', 'GigabitEthernet0/1', 'SW2', 'FastEthernet0/24'),
    link('SW2', 'FastEthernet0/10', 'SRV1', 'FastEthernet0'),
  ]);
}
export const twoLanLayout = {
  width: 980, height: 300,
  nodes: {
    PC1: { x: 80, y: 160, type: 'pc', sub: '192.168.10.10/24' },
    SW1: { x: 270, y: 160, type: 'switch', sub: 'Access · users' },
    R1: { x: 490, y: 160, type: 'router', sub: 'Inter-LAN router' },
    SW2: { x: 710, y: 160, type: 'switch', sub: 'Access · servers' },
    SRV1: { x: 900, y: 160, type: 'server', sub: '10.10.20.10/24 · files, intranet' },
  },
  zones: [
    { x: 20, y: 40, w: 370, h: 230, label: 'USERS · 192.168.10.0/24' },
    { x: 590, y: 40, w: 370, h: 230, label: 'SERVERS · 10.10.20.0/24' },
  ],
  linkLabels: { 'R1:GigabitEthernet0/0': '192.168.10.1', 'R1:GigabitEthernet0/1': '10.10.20.1' },
};
